/**
 * EmailTemplateOverviewPage — panoramica visuale dello stato di
 * personalizzazione per tutte le email editabili (16 tipi × 31 paesi).
 *
 * Option A (Fabrizio CR 10/08/2026) — con 16 tipi editabili e fino a
 * 31 paesi ciascuno, non c'era modo veloce di capire "dove hanno
 * modificato la copy Legal/Marketing". Questa pagina risolve:
 *
 *  • Matrice ad alta densità: 1 riga = 1 tipo email, ogni cella = 1
 *    paese supportato. Verde = testo di default, rosso = override.
 *  • Filtro "Solo modificati" per zoommare sui template custom.
 *  • Click su una cella custom → apre il modal diff line-by-line vs default.
 *  • Contatori riassuntivi in alto (X/Y personalizzati, ultima
 *    modifica per tipo).
 *
 * Available to super_admin + admin. Read-only.
 */
import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, RefreshCw, Filter, Palette, FileEdit, Search,
  ChevronRight, User, Clock, Circle, CheckCircle2,
} from "lucide-react";
import { API } from "../config";
import { EmailTemplateDiffModal } from "../components/admin/EmailTemplateDiffModal";

// Flag emoji — same table as EmailPreviewPage.
const FLAGS = {
  IT: "🇮🇹", UK: "🇬🇧", FR: "🇫🇷", ES: "🇪🇸", DE: "🇩🇪",
  NL: "🇳🇱", PT: "🇵🇹", PL: "🇵🇱", SE: "🇸🇪", NO: "🇳🇴",
  FI: "🇫🇮", DK: "🇩🇰", CZ: "🇨🇿", GR: "🇬🇷", HU: "🇭🇺",
  RO: "🇷🇴", SK: "🇸🇰", SI: "🇸🇮", HR: "🇭🇷", BG: "🇧🇬",
  EE: "🇪🇪", LT: "🇱🇹", LV: "🇱🇻", MT: "🇲🇹", AT: "🇦🇹",
  BE: "🇧🇪", CH: "🇨🇭", GBZ: "🇬🇮", IRL: "🇮🇪", LU: "🇱🇺", CY: "🇨🇾",
};

const formatRelativeTime = (iso) => {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  const now = Date.now();
  const delta = now - then;
  const min = Math.floor(delta / 60000);
  const hr = Math.floor(delta / 3600000);
  const day = Math.floor(delta / 86400000);
  if (day > 1) return `${day} gg fa`;
  if (day === 1) return "ieri";
  if (hr > 1) return `${hr} ore fa`;
  if (hr === 1) return "1 ora fa";
  if (min > 1) return `${min} min fa`;
  return "pochi minuti fa";
};

const EmailTemplateOverviewPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // "all" | "customized_only"
  const [search, setSearch] = useState("");
  const [openDiff, setOpenDiff] = useState(null); // { email_type, country }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/email-templates/overview`);
      setData(r.data);
    } catch (e) {
      toast.error("Impossibile caricare la panoramica template");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filteredRows = useMemo(() => {
    if (!data?.rows) return [];
    let rows = data.rows;
    if (filter === "customized_only") {
      rows = rows.filter((r) => r.customized_count > 0);
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      rows = rows.filter(
        (r) => r.email_type.toLowerCase().includes(q) ||
               r.label.toLowerCase().includes(q),
      );
    }
    return rows;
  }, [data, filter, search]);

  return (
    <div className="min-h-screen bg-black text-white p-6" data-testid="email-template-overview-page">
      <div className="max-w-[1400px] mx-auto">
        {/* ── Header ────────────────────────────────────────────── */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-lojack-blue-500/20 border border-lojack-blue-500/30 flex items-center justify-center">
                <Palette size={20} className="text-lojack-blue-400" />
              </div>
              <div>
                <h1 className="text-2xl font-semibold">Panoramica Personalizzazioni Email</h1>
                <p className="text-gray-400 text-sm">
                  Vista rapida di quali template hanno testo custom rispetto al default di piattaforma.
                </p>
              </div>
            </div>
          </div>
          <button
            onClick={load}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-lojack-blue-600 hover:bg-lojack-blue-700 text-white text-sm font-medium disabled:opacity-50 transition"
            data-testid="overview-refresh-btn"
          >
            {loading
              ? <Loader2 size={14} className="animate-spin" />
              : <RefreshCw size={14} />}
            Ricarica
          </button>
        </div>

        {/* ── Summary badges ───────────────────────────────────── */}
        {data?.totals && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="overview-totals">
            <SummaryCard
              label="Tipi email editabili"
              value={data.totals.email_types}
              testid="totals-email-types"
            />
            <SummaryCard
              label="Combinazioni totali"
              value={data.totals.total_cells}
              hint="tipo × paese"
              testid="totals-cells"
            />
            <SummaryCard
              label="Personalizzate"
              value={data.totals.customized_cells}
              tone={data.totals.customized_cells > 0 ? "positive" : "neutral"}
              hint={`${data.totals.customization_percent}% del totale`}
              testid="totals-customized"
            />
            <SummaryCard
              label="Su default piattaforma"
              value={data.totals.default_cells}
              tone="neutral"
              hint="testi non modificati"
              testid="totals-defaults"
            />
          </div>
        )}

        {/* ── Filtri ──────────────────────────────────────────── */}
        <div className="flex items-center gap-3 mb-4">
          <div className="flex items-center gap-1 bg-[#111] border border-gray-800 rounded-lg p-1" data-testid="overview-filter">
            <button
              onClick={() => setFilter("all")}
              className={`text-xs px-3 py-1.5 rounded-md transition ${filter === "all" ? "bg-lojack-blue-600 text-white" : "text-gray-400 hover:text-white"}`}
              data-testid="filter-all"
            >
              <Filter size={11} className="inline mr-1" />
              Tutti ({data?.rows?.length ?? 0})
            </button>
            <button
              onClick={() => setFilter("customized_only")}
              className={`text-xs px-3 py-1.5 rounded-md transition ${filter === "customized_only" ? "bg-lojack-blue-600 text-white" : "text-gray-400 hover:text-white"}`}
              data-testid="filter-customized"
            >
              <FileEdit size={11} className="inline mr-1" />
              Solo modificati ({(data?.rows || []).filter((r) => r.customized_count > 0).length})
            </button>
          </div>
          <div className="relative flex-1 max-w-xs">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cerca tipo email…"
              className="w-full bg-[#111] border border-gray-800 rounded-lg pl-9 pr-3 py-2 text-xs text-white focus:border-lojack-blue-500 outline-none"
              data-testid="overview-search"
            />
          </div>
          <div className="text-[10px] text-gray-500 uppercase tracking-wider flex items-center gap-3 ml-auto">
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-sm bg-emerald-500/25 border border-emerald-500/40" />
              default
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-sm bg-rose-500/25 border border-rose-500/40" />
              custom
            </span>
          </div>
        </div>

        {/* ── Loading / Empty / Grid ─────────────────────────── */}
        {loading && (
          <div className="flex justify-center py-24">
            <Loader2 size={28} className="animate-spin text-gray-600" />
          </div>
        )}

        {!loading && filteredRows.length === 0 && (
          <div className="text-center py-16 text-gray-500 text-sm bg-[#0a0a0a] border border-gray-900 rounded-xl">
            {filter === "customized_only"
              ? "Nessun template è stato ancora personalizzato — tutti usano il testo di default della piattaforma."
              : "Nessun tipo email corrisponde alla ricerca."}
          </div>
        )}

        {!loading && filteredRows.length > 0 && (
          <div className="space-y-2" data-testid="overview-rows">
            {filteredRows.map((row) => (
              <RowCard
                key={row.email_type}
                row={row}
                onCellClick={(country) => setOpenDiff({ email_type: row.email_type, country })}
              />
            ))}
          </div>
        )}
      </div>

      {openDiff && (
        <EmailTemplateDiffModal
          emailType={openDiff.email_type}
          country={openDiff.country}
          onClose={() => setOpenDiff(null)}
          onReset={() => { setOpenDiff(null); load(); }}
        />
      )}
    </div>
  );
};

// ── Sub-components ────────────────────────────────────────────────

const SummaryCard = ({ label, value, hint, tone = "neutral", testid }) => {
  const toneClasses = tone === "positive"
    ? "border-emerald-500/30 bg-emerald-500/5"
    : "border-gray-800 bg-[#0a0a0a]";
  const valueColor = tone === "positive" ? "text-emerald-400" : "text-white";
  return (
    <div className={`border ${toneClasses} rounded-xl p-4`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1">
        {label}
      </div>
      <div className={`text-2xl font-bold ${valueColor}`}>{value}</div>
      {hint && (
        <div className="text-[10px] text-gray-500 mt-0.5">{hint}</div>
      )}
    </div>
  );
};

const RowCard = ({ row, onCellClick }) => {
  const isCustomized = row.customized_count > 0;
  return (
    <div
      className={`bg-[#0a0a0a] border rounded-xl p-4 transition ${
        isCustomized ? "border-lojack-blue-500/30" : "border-gray-900"
      }`}
      data-testid={`overview-row-${row.email_type}`}
    >
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1">
            <code className="text-[10px] font-mono text-lojack-blue-400 bg-lojack-blue-500/10 px-1.5 py-0.5 rounded">
              {row.email_type}
            </code>
            <span className="text-xs text-gray-400">
              {row.field_count} campi editabili · {row.country_count} paesi
            </span>
          </div>
          <div className="text-sm text-white truncate">{row.label}</div>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          {isCustomized && row.latest_updated_by && (
            <div className="text-right hidden md:block">
              <div className="text-[10px] text-gray-500 flex items-center gap-1 justify-end">
                <User size={9} />
                {row.latest_updated_by}
              </div>
              <div className="text-[10px] text-gray-500 flex items-center gap-1 justify-end">
                <Clock size={9} />
                {formatRelativeTime(row.latest_updated_at)}
              </div>
            </div>
          )}
          <div className={`text-xs px-3 py-1.5 rounded-lg font-semibold flex items-center gap-1.5 ${
            isCustomized
              ? "bg-lojack-blue-500/20 text-lojack-blue-300 border border-lojack-blue-500/30"
              : "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20"
          }`}>
            {isCustomized ? (
              <>
                <FileEdit size={11} />
                {row.customized_count}/{row.country_count} custom
              </>
            ) : (
              <>
                <CheckCircle2 size={11} />
                Tutti default
              </>
            )}
          </div>
        </div>
      </div>

      {/* Cell grid: 1 cella per paese supportato dal tipo */}
      <div className="grid grid-cols-8 sm:grid-cols-12 lg:grid-cols-16 xl:grid-cols-20 gap-1.5" data-testid={`overview-cells-${row.email_type}`}>
        {row.cells.map((cell) => (
          <button
            key={cell.country}
            onClick={cell.customized ? () => onCellClick(cell.country) : undefined}
            disabled={!cell.customized}
            title={cell.customized
              ? `${cell.country} — ${cell.custom_field_count} campi custom · Click per diff`
              : `${cell.country} — testo di default`}
            className={`aspect-square rounded-md flex items-center justify-center text-[10px] font-mono transition ${
              cell.customized
                ? "bg-rose-500/20 border border-rose-500/40 text-rose-100 hover:bg-rose-500/40 cursor-pointer"
                : "bg-emerald-500/10 border border-emerald-500/20 text-emerald-300/60 cursor-default"
            }`}
            data-testid={`overview-cell-${row.email_type}-${cell.country}`}
          >
            <span className="flex flex-col items-center leading-none">
              <span className="text-[13px]">{FLAGS[cell.country] || "🏳️"}</span>
              <span className="text-[8px] mt-0.5">{cell.country}</span>
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};

export default EmailTemplateOverviewPage;
